import { useCallback, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import { IRestaurantNode } from 'generated/rbi-graphql';
import { useNavigation } from 'hooks/navigation/use-navigation';
import { useDefaultServiceMode, useSelectRestaurant } from 'hooks/store-card';
import { useServiceModeStatus } from 'hooks/use-service-mode-status';
import useStoreCardButtonOptions from 'hooks/use-store-card-button-options';
import { actions, useAppDispatch } from 'state/global-state';
import { StoreCardButtonOptions } from 'state/launchdarkly/variations';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { useOrderContext } from 'state/order';
import { useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import { EventName, emitEvent } from 'utils/event-hub';
import { AttributeName } from 'utils/performance';
import { routes } from 'utils/routing';

import { ACTION_BUTTON_ICONS } from '../constants/action-button-icons';
import { IActionOption } from '../types';

interface IUseStoreActionOptions {
  (options: {
    restaurant: IRestaurantNode;
    setShowChangeRegionModal: (showModal: boolean) => void;
  }): IActionOption[];
}

export const useStoreActionOptions: IUseStoreActionOptions = ({
  restaurant,
  setShowChangeRegionModal,
}) => {
  const { selectRestaurant } = useSelectRestaurant({
    restaurant,
  });
  const { fetchingPosData } = useOrderContext();
  const storeCardButtonOptions = useStoreCardButtonOptions();
  const { logEvent, logRBIEvent } = useMParticleContext();
  const { navigate } = useNavigation();
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const { store: currentStore } = useStoreContext();

  const { restaurantCanBeSelected } = useServiceModeStatus(restaurant);

  const { setServiceMode } = useServiceModeContext();
  const defaultServiceMode = useDefaultServiceMode({ restaurant });
  const [selectedButtonOption, setSelectedButtonOption] = useState(StoreCardButtonOptions.INFO);

  const logClick = useCallback(
    (name: StoreCardButtonOptions) => {
      logEvent(CustomEventNames.BUTTON_CLICK_STORE_BUTTON, EventTypes.Other, {
        Name: name,
        StoreID: restaurant.number,
      });
    },
    [logEvent, restaurant]
  );

  const logRBIStoreSelected = useCallback(() => {
    logRBIEvent({
      name: 'Store Selected',
      type: EventTypes.Other,
      attributes: {
        restaurantId: restaurant._id || '',
        restaurantAddress: restaurant.physicalAddress?.address1 || '',
        restaurantCity: restaurant.physicalAddress?.city || '',
        restaurantZip: restaurant.physicalAddress?.postalCode || '',
        restaurantState: restaurant.physicalAddress?.stateProvince || '',
        restaurantCountry: restaurant.physicalAddress?.country || '',
        storeId: restaurant.storeId || '',
      },
    });
  }, [logRBIEvent, restaurant]);

  /**
   * DEFINE BUTTON CLICK HANDLERS
   */
  const onInfoClick = useCallback(() => {
    logClick(StoreCardButtonOptions.INFO);
    setSelectedButtonOption(StoreCardButtonOptions.INFO);
    navigate(`${routes.store}/${restaurant._id}`);
  }, [logClick, navigate, restaurant._id]);

  const onOffersClick = useCallback(() => {
    logClick(StoreCardButtonOptions.OFFERS);
    setSelectedButtonOption(StoreCardButtonOptions.OFFERS);
    emitEvent(EventName.RESTAURANT_SELECTED_ON_OFFERS, {
      isStoreChange: currentStore?._id !== restaurant?._id,
      attributes: [
        {
          name: AttributeName.RESTAURANT_ID,
          value: restaurant.id || '',
        },
      ],
    });

    selectRestaurant({
      callback: () => {
        setServiceMode(defaultServiceMode);
        navigate(routes.offers);
      },
    });
  }, [
    currentStore?._id,
    defaultServiceMode,
    logClick,
    navigate,
    restaurant?._id,
    restaurant.id,
    selectRestaurant,
    setServiceMode,
  ]);

  const onOrderClick = useCallback(() => {
    selectRestaurant();

    logClick(StoreCardButtonOptions.ORDER);
    setSelectedButtonOption(StoreCardButtonOptions.ORDER);

    // Set shouldRefetchOffers to true on restaurant selection
    dispatch(actions.loyalty.setShouldRefetchOffers(true));

    emitEvent(EventName.RESTAURANT_SELECTED_ON_ORDER, {
      isStoreChange: currentStore?._id !== restaurant?._id,
      attributes: [
        {
          name: AttributeName.RESTAURANT_ID,
          value: restaurant.id || '',
        },
      ],
    });

    logRBIStoreSelected();
  }, [logClick, logRBIStoreSelected, dispatch, restaurant, currentStore, selectRestaurant]);

  /**
   * MAP BUTTON OPTIONS TO CLICK HANDLER
   */
  const actionButtonClickHandlers = useMemo(
    () => ({
      [StoreCardButtonOptions.INFO]: onInfoClick,
      [StoreCardButtonOptions.OFFERS]: onOffersClick,
      [StoreCardButtonOptions.ORDER]: onOrderClick,
    }),
    [onInfoClick, onOffersClick, onOrderClick]
  );

  /**
   * MAP BUTTON OPTIONS TO TEXT
   */
  const actionButtonText = useMemo(() => {
    return {
      [StoreCardButtonOptions.INFO]: formatMessage({ id: 'info' }),
      [StoreCardButtonOptions.OFFERS]: formatMessage({ id: 'offers' }),
      [StoreCardButtonOptions.ORDER]: formatMessage({ id: 'order' }),
    };
  }, [formatMessage]);

  /**
   * CREATE STORE ACTION OPTIONS
   */
  return useMemo(() => {
    const getDisabled = (option: StoreCardButtonOptions) => {
      switch (option) {
        case StoreCardButtonOptions.ORDER:
        case StoreCardButtonOptions.OFFERS:
          return !restaurantCanBeSelected;

        default:
          return false;
      }
    };

    const getLoading = (option: StoreCardButtonOptions) => {
      return fetchingPosData && selectedButtonOption === option;
    };

    return storeCardButtonOptions.map(option => {
      return {
        id: option,
        text: actionButtonText[option],
        onClick: actionButtonClickHandlers[option],
        icon: ACTION_BUTTON_ICONS[option],
        disabled: getDisabled(option),
        loading: getLoading(option),
      };
    });
  }, [
    actionButtonClickHandlers,
    actionButtonText,
    fetchingPosData,
    restaurantCanBeSelected,
    selectedButtonOption,
    storeCardButtonOptions,
  ]);
};
